import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useFormDisable } from '../form-new/form'
import { FormNewLabel } from '../form-new/form-label'
import { FieldSectionContext, UnsavedFieldsContext } from '../form-new/field-meta'
import { sanitizeIconSVG } from '../svg/sanitize-svg'
import { ProviderLogo, IconSVG } from './provider-logo'

// The connection's Icon (SSO round 3 section 3; rapid-api-auth
// common/provider-icon): one choice, saved with the page.
//
//   auto          auth picks the brand icon from the issuer's address
//                 (login.microsoftonline.com -> Microsoft, ...; none ->
//                 Building). The option names what it picks for the issuer
//                 in the form, as typed: "Automatic (Microsoft)".
//   preset:<key>  one icon of auth's library
//   custom        an SVG staff upload or paste
//
// Form fields: iconSource and iconSVGCustom (the uploaded SVG, kept while
// another source is chosen so staff can switch back). The library comes from
// GET /provider-icon/<issuer> (onLoadIcons). A custom SVG is drawn through
// the same sanitiser as every provider icon (sanitizeIconSVG), and auth
// refuses a scriptable one on save with the reason.

export const ICON_UPLOAD_MAX = 64 * 1024
const LOAD_DELAY_MS = 350

const AUTO = 'auto'
const CUSTOM = 'custom'
const presetSource = key => `preset:${key}`

// What the chosen source draws: { svg, svgDark, label }.
export const chosenIcon = ({ source, custom, library, loaded }) => {
  const presets = (library && library.presets) || []
  const byKey = key => presets.find(p => p.key === key)
  if (source === CUSTOM) return { svg: custom || '', svgDark: '', label: 'Custom' }
  const key = source === AUTO ? library && library.auto && library.auto.key : String(source || '').replace(/^preset:/, '')
  const preset = key && byKey(key)
  if (preset) return { svg: preset.svg, svgDark: preset.svgDark || '', label: preset.label }
  // Before the library answers: the icon the connection was loaded with,
  // when the choice is still the loaded one.
  if (loaded && loaded.iconSource === source) return { svg: loaded.iconSVG || '', svgDark: loaded.iconSVGDark || '', label: '' }
  return { svg: '', svgDark: '', label: '' }
}

// Reads an uploaded file as text; refuses what can't be an icon.
export const readIconFile = file => new Promise((resolve, reject) => {
  if (!file) return reject(new Error('Choose an SVG file.'))
  if (file.size > ICON_UPLOAD_MAX) return reject(new Error('That file is too large for an icon. Use an SVG under 64 KB.'))
  const reader = new FileReader()
  reader.onload = () => resolve(String(reader.result || ''))
  reader.onerror = () => reject(new Error('That file could not be read.'))
  reader.readAsText(file)
})

// What auth refuses in a stored icon (rapid-api-auth service/provider
// icon.go sanitizeIconSVG), said here before the save instead of after it.
const REFUSED = [
  [/<script\b/i, 'Remove the <script> element: icons can\'t run scripts.'],
  [/javascript:/i, 'Remove the javascript: link: icons can\'t run scripts.'],
  [/\bon\w+\s*=/i, 'Remove the event handler attributes (onload=, onclick=, ...): icons can\'t run scripts.'],
  [/<foreignobject\b/i, 'Remove the <foreignObject> element.'],
  [/(?:xlink:)?href\s*=\s*["'](?!\s*#)[^"']+["']/i, 'Remove the links to other files (href): an icon must be one self-contained SVG.'],
  [/<(?:set|animate)\b[^>]*\battributeName\s*=\s*["'](?:on\w+|(?:xlink:)?href)["']/i, 'Remove the animation that changes a link or an event handler.'],
  [/<style\b[^>]*>[\s\S]*?(?:@import|url\s*\()[\s\S]*?<\/style\s*>/i, 'Remove @import and url() from the <style> element.']
]

// A pasted or uploaded SVG: one auth will store and the sanitiser leaves
// something of, else what's wrong with it.
export const iconProblem = svg => {
  const text = String(svg || '').trim()
  if (!text) return 'Upload or paste an SVG.'
  if (!/<svg[\s>]/i.test(text)) return 'That isn\'t an SVG. Upload a .svg file or paste markup that starts with <svg.'
  const refused = REFUSED.find(([pattern]) => pattern.test(text))
  if (refused) return refused[1]
  if (!/<svg[\s>]/i.test(sanitizeIconSVG(text))) return 'Nothing in that SVG can be shown.'
  return null
}

// What the apps' SVG reader (Unity Vector Graphics) may not draw (SSO round
// 3 section 4): said as a note on a custom icon, not refused -- the web
// draws it, and the apps keep their default icon when they can't.
const APP_UNSUPPORTED = [
  [/<style\b/i, '<style>'], [/gradient/i, 'gradients'], [/<mask\b/i, '<mask>'], [/<filter\b/i, 'filters'],
  [/<image\b/i, '<image>'], [/currentColor/i, 'currentColor'], [/var\(/i, 'CSS variables'], [/<text\b/i, 'text']
]
export const appDrawNote = svg => {
  const found = APP_UNSUPPORTED.filter(([pattern]) => pattern.test(String(svg || ''))).map(([, name]) => name)
  return found.length ? `The EducationXR apps may not draw this icon (${found.join(', ')}). Plain shapes and fill colours always work.` : null
}

// One way the sign-in button looks, on a light or a dark page.
const Preview = ({ theme, svg, label }) =>
  <div className={`sw-icon-preview sw-icon-preview-${theme}`} data-testid={`icon-preview-${theme}`}>
    <span className="sw-icon-preview-k">{theme === 'light' ? 'Light' : 'Dark'}</span>
    <span className="sw-icon-preview-button">
      {svg ? <IconSVG svg={svg} className="sw-icon-preview-svg" /> : <span className="sw-icon-preview-svg sw-icon-preview-empty" />}
      <span className="sw-icon-preview-label">{label}</span>
    </span>
  </div>

export const IconControl = ({ onLoadIcons, loaded, staffLabel }) => {
  const { register, setValue, control } = useFormContext()
  const formDisabled = useFormDisable()
  const source = useWatch({ control, name: 'iconSource' }) || AUTO
  const custom = useWatch({ control, name: 'iconSVGCustom' }) || ''
  const issuer = useWatch({ control, name: 'issure' }) || ''
  const buttonText = useWatch({ control, name: 'displayName' }) || 'Sign in'
  const [library, setLibrary] = useState(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [pasting, setPasting] = useState(false)
  const [problem, setProblem] = useState(null)
  const fileRef = useRef(null)
  const request = useRef(0)

  // The custom slot is a field with no visible label of its own: name it
  // for the unsaved-changes list, as a value never printed there.
  const unsaved = useContext(UnsavedFieldsContext)
  const section = useContext(FieldSectionContext)
  const registerMeta = unsaved && unsaved.register
  useEffect(() => {
    if (!registerMeta) return undefined
    return registerMeta('iconSVGCustom', { label: 'Custom icon', sectionId: section && section.id, sectionTitle: section && section.title, secret: true })
  }, [registerMeta, section])

  // The library, and what Automatic picks for the issuer as typed. The
  // loader is read through a ref: a caller that hands a new function on
  // every render must not reload the library on every render.
  const loader = useRef(onLoadIcons)
  loader.current = onLoadIcons
  const canLoad = Boolean(onLoadIcons)
  useEffect(() => {
    if (!canLoad) return undefined
    const mine = ++request.current
    const timer = setTimeout(async () => {
      try {
        const result = await loader.current(String(issuer).trim())
        if (mine !== request.current) return
        setLibrary(result || null)
        setLoadFailed(false)
      } catch (err) {
        if (mine === request.current) setLoadFailed(true)
      }
    }, library ? LOAD_DELAY_MS : 0)
    return () => clearTimeout(timer)
  }, [issuer, canLoad]) // eslint-disable-line react-hooks/exhaustive-deps

  const presets = (library && library.presets) || []
  const autoLabel = library && library.auto ? `Automatic (${library.auto.label})` : 'Automatic'
  const shown = useMemo(() => chosenIcon({ source, custom, library, loaded }), [source, custom, library, loaded])
  const autoIcon = useMemo(() => chosenIcon({ source: AUTO, library, loaded }), [library, loaded])

  const choose = value => setValue('iconSource', value, { shouldDirty: true })
  const applyCustom = svg => {
    const why = iconProblem(svg)
    setProblem(why)
    if (why) return false
    setValue('iconSVGCustom', String(svg).trim(), { shouldDirty: true })
    choose(CUSTOM)
    return true
  }

  const handleFile = async event => {
    const file = event.target.files && event.target.files[0]
    event.target.value = ''
    if (!file) return
    try {
      applyCustom(await readIconFile(file))
    } catch (err) {
      setProblem(err.message)
    }
  }

  const option = (value, label, svg, svgDark) =>
    <label key={value} className={`sw-icon-option${source === value ? ' sw-icon-option-on' : ''}`}>
      <input type="radio" className="visually-hidden" value={value} {...register('iconSource')} disabled={formDisabled} />
      <ProviderLogo iconSVG={svg} iconSVGDark={svgDark} />
      <span className="sw-icon-option-label">{label}</span>
    </label>

  return (
    <FormNewLabel name="iconSource" label={staffLabel ? staffLabel('Icon') : 'Icon'}
      subtext="Shown on the sign-in button and as the logo above. Automatic picks it from the issuer's address.">
      {[<div key="icon-control" className="sw-icon-control" data-field="iconSource">
        <div className="sw-icon-options" role="radiogroup" aria-labelledby="iconSource-label">
          {option(AUTO, autoLabel, autoIcon.svg, autoIcon.svgDark)}
          {presets.map(p => option(presetSource(p.key), p.label, p.svg, p.svgDark))}
          {(custom || source === CUSTOM) && option(CUSTOM, 'Custom', custom, '')}
        </div>
        {loadFailed && <div className="sw-hint sw-warn mt-1" role="note">The icon library couldn't be loaded. You can still upload an SVG.</div>}

        <div className="sw-icon-custom">
          <input ref={fileRef} type="file" accept=".svg,image/svg+xml" className="visually-hidden" aria-label="Upload SVG" onChange={handleFile} disabled={formDisabled} data-testid="icon-file" />
          <button type="button" className="btn sw-btn btn-sm" disabled={formDisabled} onClick={() => fileRef.current && fileRef.current.click()}>Upload SVG…</button>
          <button type="button" className="btn btn-link btn-sm" disabled={formDisabled} aria-expanded={pasting} onClick={() => setPasting(p => !p)}>
            {pasting ? 'Close paste' : 'Paste SVG'}
          </button>
          {problem && <div className="sw-hint sw-bad w-100" role="alert">{problem}</div>}
          {pasting && <PasteBox disabled={formDisabled} initial={custom} onUse={svg => { if (applyCustom(svg)) setPasting(false) }} />}
        </div>

        {source === CUSTOM && appDrawNote(custom) && <div className="sw-hint sw-warn" role="note" data-testid="icon-app-note">{appDrawNote(custom)}</div>}

        <div className="sw-icon-previews" aria-label="Previews">
          <Preview theme="light" svg={shown.svg} label={buttonText} />
          <Preview theme="dark" svg={shown.svgDark || shown.svg} label={buttonText} />
        </div>
      </div>]}
    </FormNewLabel>
  )
}

const PasteBox = ({ disabled, initial, onUse }) => {
  const [text, setText] = useState(initial || '')
  return (
    <div className="sw-icon-paste w-100">
      <textarea className="form-control sw-mono" rows={4} aria-label="SVG markup" placeholder="<svg viewBox=&quot;0 0 24 24&quot;>…</svg>"
        value={text} onChange={e => setText(e.target.value)} disabled={disabled} />
      <button type="button" className="btn sw-btn btn-sm mt-1" disabled={disabled || !text.trim()} onClick={() => onUse(text)}>Use this SVG</button>
    </div>
  )
}
